import { Lead, GuideSection, P, UL, LI, Callout, GuideLink, Term } from './guide-ui';

/**
 * Nederlandstalige gids (locale: 'nl', logpulse.nl): wat aanvallen door
 * AI-agents op webwinkels in je logs achterlaten. Hoort bij de
 * dreigingspagina en de ChatGPT-campagne, groep C. De detecties die genoemd
 * worden bestaan als ingebouwde detectie (packages/types/src/siem/
 * builtin-detections.ts); de webshell-detectie kijkt naar de request body,
 * die niet in access logs staat en dat zegt de tekst ook.
 */
export function AiAgentsWebwinkelsGuideBody() {
  return (
    <>
      <Lead>
        Onderzoekers zagen in oktober 2026 hoe autonome AI-agents ruim 1.200
        webwinkels aanvielen, voor zo&rsquo;n 50 dollar per geslaagde hack. De
        aanvalstool is nieuw, de sporen die hij achterlaat zijn dat niet. Deze
        gids laat zien welke signalen je kunt zien en in welke logs ze staan.
      </Lead>

      <GuideSection id="wat-er-gebeurde" title="Wat er gebeurde">
        <P>
          Onderzoekers van NorForge beschreven een aanvalstool met de naam Cairn:
          AI-agents die zelfstandig webwinkels aanvallen. Bij ongeveer één op de
          tien lukte het, voor gemiddeld zo&rsquo;n 50 dollar per geslaagde
          inbraak. Het doel was een webshell, een klein script op de server dat
          de aanvaller blijvende toegang geeft (
          <GuideLink href="https://thehackernews.com/2026/10/hackers-use-autonomous-ai-agents-to.html">
            The Hacker News
          </GuideLink>
          ).
        </P>
        <P>
          Het nieuws zit in de prijs. Kost een aanval 50 dollar, dan zijn ook
          kleine webwinkels en zijprojecten de moeite waard. Het goede nieuws:
          een AI-agent moet nog steeds via HTTP werken en elke stap komt in je
          webserverlogs terecht.
        </P>
      </GuideSection>

      <GuideSection id="signalen" title="Wat de agent doet en wat jij ziet">
        <UL>
          <LI>
            <Term>De site in kaart brengen.</Term> De agent loopt de site af op
            zoek naar beheerpagina&rsquo;s, installatiescripts, back-ups en{' '}
            <code>.env</code>-bestanden. In de access logs zie je één client die
            in korte tijd veel paden opvraagt. Blijft hij op beheerpaden kloppen
            en krijgt hij 401 of 403 terug, dan gaat de ingebouwde detectie
            Admin endpoint probing af.
          </LI>
          <LI>
            <Term>Bekende lekken proberen.</Term> De agent stuurt verzoeken naar
            plugins en versies waarvoor een openbare exploit bestaat. Hier telt
            de CVE-kant: draait je winkel een plugin die op de KEV-lijst staat,
            dan heeft de poging een reële kans.
          </LI>
          <LI>
            <Term>Een webshell plaatsen.</Term> Eenmaal binnen schrijft de agent
            een script dat opdrachten uit een verzoek uitvoert. Een request body
            met <code>eval(base64_decode(</code> is een klassiek teken; daarvoor
            heeft LogPulse de detectie Web shell eval payload.
          </LI>
          <LI>
            <Term>Vanaf bekende adressen.</Term> Zulke campagnes gebruiken vaak
            IP-adressen die al op threat feeds staan. LogPulse verrijkt events
            elk uur met die feeds en verhoogt de risicoscore als een verzoek van
            zo&rsquo;n adres komt.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="welke-logs" title="Welke logs je nodig hebt">
        <P>
          Access logs van Apache of nginx in het combined-formaat dekken het in
          kaart brengen, het afspeuren van beheerpaden en de IP-adressen voor de
          feeds. Installeer de LogPulse-agent op de webserver en voeg de access
          log toe als bestand, of stuur hem vanuit je hostingpartij via de
          HTTP-API.
        </P>
        <P>
          Access logs bevatten geen request bodies. Voor de webshell-detectie
          heb je dus een bron nodig die dat wel heeft, zoals je WAF of een
          applicatielog die de body van verdachte verzoeken bewaart. Draait de
          winkel in containers, stuur dan ook een kwetsbaarheidsscan mee. Dan
          zie je welke uitgebuite lekken er op staan voordat een agent ze vindt.
        </P>
      </GuideSection>

      <GuideSection id="grenzen" title="Wat dit niet doet">
        <Callout title="Detectie is geen preventie">
          <P>
            Een web application firewall, een bijgewerkt CMS en een korte lijst
            plugins doen het meeste werk. Logs vertellen je of iemand het al
            geprobeerd heeft en of het gelukt is.
          </P>
        </Callout>
        <P>
          Lees hoe de detecties samenhangen op de pagina{' '}
          <GuideLink href="/threat-intelligence">Threat intelligence</GuideLink>,
          of begin gratis zonder creditcard.
        </P>
      </GuideSection>
    </>
  );
}
